/**
 * UK National Insurance numbers for the claim letter, following the GOV.UK
 * pattern: https://design-system.service.gov.uk/patterns/national-insurance-numbers/
 *
 * Parsed in the browser only: written into the download, never stored.
 * Never use a National Insurance number to verify identity.
 *
 * Input is permissive: upper or lower case, extra spaces and punctuation.
 * Unwanted characters are ignored before validating. The format is 2 letters,
 * 6 numbers, then A, B, C or D. The example number is QQ 12 34 56 C, which is
 * not a real issued number (do not use AB 12 34 56 C).
 */

const COMPACT_PATTERN = /^[A-Z]{2}[0-9]{6}[A-D]$/

/** Compact NINO is 9 characters; spaced display is 13 including spaces. */
export const NATIONAL_INSURANCE_COMPACT_LENGTH = 9
export const NATIONAL_INSURANCE_DISPLAY_LENGTH = 13

/** Letters and digits only, uppercased. Spaces and punctuation are dropped. */
function compactNationalInsurance(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/**
 * Live field value: uppercase, HMRC grouping (QQ 12 34 56 C), capped at 9
 * letters/digits. Unwanted characters are dropped as they type. Full format
 * is still checked on download, not while they are mid-entry.
 */
export function maskNationalInsuranceInput(raw: string): string {
  return formatNationalInsuranceNumber(
    compactNationalInsurance(raw).slice(0, NATIONAL_INSURANCE_COMPACT_LENGTH)
  )
}

/**
 * Caret after masking, so inserted spaces do not jump the cursor. Counts
 * letters/digits before the old caret and places the caret after the same
 * count in the masked string.
 */
export function nationalInsuranceCaretIndex(
  raw: string,
  selectionStart: number
): number {
  const boundedStart = Math.max(0, selectionStart)
  const alphaBefore = Math.min(
    NATIONAL_INSURANCE_COMPACT_LENGTH,
    compactNationalInsurance(raw.slice(0, boundedStart)).length
  )
  const masked = maskNationalInsuranceInput(raw)
  if (alphaBefore === 0) return 0
  let seen = 0
  for (let i = 0; i < masked.length; i++) {
    if (/[A-Z0-9]/.test(masked[i] ?? '')) {
      seen++
      if (seen === alphaBefore) return i + 1
    }
  }
  return masked.length
}

/** Compact form (QQ123456C) if the value matches the GOV.UK format. */
export function parseNationalInsuranceNumber(raw: string): string | null {
  const compact = compactNationalInsurance(raw)
  if (!COMPACT_PATTERN.test(compact)) return null
  return compact
}

/** Spaced grouping used on HMRC letters, e.g. QQ 12 34 56 C. */
export function formatNationalInsuranceNumber(compact: string): string {
  const groups = [
    compact.slice(0, 2),
    compact.slice(2, 4),
    compact.slice(4, 6),
    compact.slice(6, 8),
    compact.slice(8, 9),
  ].filter((group) => group.length > 0)
  return groups.join(' ')
}
